import React, { lazy, useEffect, useMemo, useState } from "react";
import { Cross } from "../../../components/common/Icons";
import * as styles from "./FilterPanel.module.scss";

import Modal from "react-modal";
import SSRSuspense from "../../../components/SSRSuspense/SSRSuspense";
import { useDogSearchApi } from "../../../context";
import AllFilters from "../AllFilters";
import BreedFilters from "../BreedFilters";
import "./ModalStyles.css";
import { isSpecificSite } from "../../../utils/isSpecificSite";

export interface AllFiltersProps {
	isListingPage: boolean;
	shouldInit?: boolean;
	shouldMaintainFilters?: boolean;
	allCentres: Queries.RehomingCentreDataFragment[];
}

// biome-ignore lint/suspicious/noExplicitAny: Legit any
const trackFilters = (data: any) => {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({ ...data });
};

//! Any changes in this file may need to be replicated in ./FilterPanel.ie.tsx
export const FilterPanel: React.FC<AllFiltersProps> = ({
	isListingPage,
	shouldMaintainFilters = false,
	allCentres,
	shouldInit = true,
}) => {
	isSpecificSite("uk", "FilterPanel");

	const [hasInit, setHasInit] = useState(false);
	const {
		showFilters,
		lastView,
		selectedBreeds,
		selectedAges,
		selectedSizes,
		selectedGenders,
		selectedLivesWith,
		selectedCentres,
		selectedDayRange,
		currentDistance,
		onInitFilters,
		useDogSearchQuery,
		onChangeFilterView,
		onResetFilters,
	} = useDogSearchApi();

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		Modal.setAppElement(document.getElementById("main-content"));
		useDogSearchQuery();
	}, []);

	const headerValues = () => {
		switch (showFilters) {
			case "Breeds":
				return {
					title: "Breeds",
					onClose: () => onChangeFilterView(isListingPage ? "Any" : "None"),
				};
			case "SizeGuide":
				return {
					title: "Size Guide",
					onClose: () => onChangeFilterView(lastView),
				};
			default:
				return {
					title: "Filters",
					onClose: () => {
						onChangeFilterView("None");
						trackFilters({
							event: "rehoming_search",
							rehoming_location: selectedCentres?.toString(), // location selected
							rehoming_breed: selectedBreeds?.toString(), // dog breed selected, comma separated list for multiple selections
							rehoming_age: selectedAges?.toString(), // dog age selected, comma separated list for multiple selections
							rehoming_size: selectedSizes?.toString(), // dog size selected, comma separated list for multiple selections
							rehoming_gender: selectedGenders?.toString(), // gender selected
							rehoming_live_with: selectedLivesWith?.toString(), // may live with preference selected, comma separated listfor multiple selections
							rehoming_days_added: selectedDayRange?.toString(), // days since added selection
							rehoming_distance: currentDistance?.toString(), // The distance the user is willing to travel
						});
					},
				};
		}
	};

	useEffect(() => {
		if (shouldInit && !hasInit) {
			const mappedCentres: DogsTrust.RehomingCentreLocation[] = allCentres.map(
				(centre) => ({
					title: centre.title,
					centreCode: centre.relationships.field_centre_code.name,
					latitude: Number.parseFloat(centre.field_latitude),
					longitude: Number.parseFloat(centre.field_longitude),
				}),
			);
			onInitFilters(isListingPage, shouldMaintainFilters, mappedCentres);
			setHasInit(true);
		}
	}, [
		allCentres,
		shouldInit,
		hasInit,
		isListingPage,
		onInitFilters,
		shouldMaintainFilters,
	]);

	useEffect(() => {
		if (showFilters === "None" || showFilters === "Initial") {
			document.getElementsByTagName("body")[0].style.overflow = "scroll";
		} else {
			document.getElementsByTagName("body")[0].style.overflow = "hidden";
		}
		return () => {
			document.getElementsByTagName("body")[0].style.overflow = "scroll";
		};
	}, [showFilters]);

	const FilterComponent = useMemo(
		() => () => {
			switch (showFilters) {
				case "Breeds":
					return <BreedFilters />;
				case "SizeGuide": {
					const SizeGuide = lazy(
						() => import("../../../containers/slidingDraws/SizeGuide"),
					);
					return (
						<SSRSuspense>
							<SizeGuide hideSizeGuide={() => onChangeFilterView(lastView)} />
						</SSRSuspense>
					);
				}
				default:
					return <AllFilters />;
			}
		},
		[showFilters, lastView, onChangeFilterView],
	);

	const isOpen = showFilters !== "Initial" && showFilters !== "None";
	return (
		<Modal
			isOpen={isOpen}
			preventScroll
			onRequestClose={() => onChangeFilterView("None")}
			shouldCloseOnEsc
			shouldCloseOnOverlayClick
			shouldFocusAfterRender
			shouldReturnFocusAfterClose
			style={{
				overlay: {
					position: "fixed",
					display: "flex",
					justifyContent: "flex-end",
					height: "100%",
					width: "100%",
					padding: 0,
					margin: 0,
					zIndex: 10001,
					boxSizing: "border-box",
					backgroundColor: "rgba(0, 0, 0, 0.5)",
				},
				content: {
					position: "relative",
					height: "100%",
					top: 0,
					maxWidth: 700,
					zIndex: 10002,
					padding: 0,
					margin: 0,
					right: 0,
					inset: 0,
				},
			}}
		>
			<div className={styles.component}>
				<div className={styles.header}>
					{shouldInit ? (
						<button
							type="button"
							className={styles.headersection}
							style={{ cursor: "pointer" }}
							onClick={() => onResetFilters()}
							tabIndex={0}
							aria-label="Reset filters"
							onKeyDown={(e) => e.key === "Enter" && onResetFilters()}
						>
							Reset
						</button>
					) : (
						<div className={styles.headersection} />
					)}
					<h2 className={styles.headersection}>{headerValues().title}</h2>
					<div className={styles.headersection}>
						<button
							type="button"
							className={styles.closefilters}
							onClick={headerValues().onClose}
							tabIndex={0}
							aria-label="Close filters"
							onKeyDown={(e) => e.key === "Enter" && headerValues().onClose()}
						>
							<Cross />
						</button>
					</div>
				</div>
				<FilterComponent />
			</div>
		</Modal>
	);
};
